import { useEffect, useMemo } from "react";
import { Check } from "lucide-react";
import CTAButton from "./CTAButton";
import CountdownTimer from "./CountdownTimer";
import heroPhoto from "@/assets/hero-photo.webp";
import { getOrAssignVariant, trackVariantEvent } from "@/lib/abTest";
import { recordAbView, startTimeTracking } from "@/lib/abTracking";
import VTurbPlayer from "./VTurbPlayer";

const HeroSection = () => {
  const bullets = [
    "Planejamento para ganhar +15k/mês",
    "Mais de 46 milhões em vendas 1 a 1*",
    "Como quebrar as principais objeções",
    "Como começar do zero e sem experiência",
    "Certificado de conclusão da Imersão (gerado no dia seguinte do evento)",
  ];

  const variant = useMemo(() => getOrAssignVariant("hero_headline", ["B"]), []);

  useEffect(() => {
    trackVariantEvent("hero_view", "hero_headline", variant);
    recordAbView(variant);
    const stop = startTimeTracking(variant);
    return () => {
      stop?.();
    };
  }, [variant]);

  const renderHeadline = () => {
    if (variant === "B" || variant === "D") {
      return (
        <>
          <span className="text-gradient-orange-animated">Em um dia ao vivo</span>, aprenda o passo a passo na prática,{" "}
          <br />
          como fechar vendas como um <span className="text-gradient-orange-animated">Closer sênior</span> <br />e ganhar{" "}
          <span className="text-gradient-orange-animated">15k todos os meses</span>
        </>
      );
    }
    return (
      <>
        <span className="text-gradient-orange-animated">Em um dia ao vivo</span>, nós vamos construir e planejar a sua
        carreira como <span className="text-gradient-orange-animated">Closer Digital para ganhar 15k/mês</span>
      </>
    );
  };

  // C and D use the VSL layout (no photo, VSL below headline, bullets below CTA)
  const isVslLayout = variant === "C" || variant === "D";
  // C uses headline A; D uses headline B (handled inside renderHeadline via variant check above:
  // for D we already return the B headline; for C we fall through to the A headline.)

  if (isVslLayout) {
    return (
      <section className="pt-3 md:pt-6 pb-6 md:pb-12" id="hero" data-headline-variant={variant}>
        <div className="container max-w-3xl mx-auto text-center">
          <h1
            className={`${variant === "D" ? "text-[18px] md:text-[2.6rem] mb-5 md:mb-6" : "text-[18px] md:text-5xl mb-4 md:mb-6"} font-black leading-tight text-foreground drop-shadow-[0_2px_10px_hsl(22_93%_49%_/0.15)]`}
          >
            {renderHeadline()}
          </h1>

          {/* VSL */}
          <div className="mb-6 md:mb-8">
            <VTurbPlayer />
          </div>

          {/* Price (full size) above CTA */}
          <div className="flex flex-col items-center gap-3 mb-3">
            <p className="text-muted-foreground text-base md:text-lg">
              <span className="line-through">De R$479</span>{" "}
              <span className="text-3xl md:text-4xl font-black text-[hsl(var(--cta))] drop-shadow-[0_0_12px_hsl(142_71%_38%_/_0.5)]">
                por R$27,00
              </span>
            </p>
          </div>

          {/* CTA + countdown below */}
          <div className="flex flex-col items-center gap-3 mb-10">
            <CTAButton
              size="lg"
              href="#cta-final"
              className="text-xl md:text-2xl [text-shadow:_0_1px_4px_rgba(0,0,0,0.25)]"
            >
              Garantir Ingresso | 1º Lote
            </CTAButton>
          </div>

          {/* Bullets below CTA */}
          <div className="text-left max-w-md mx-auto mb-0 md:mb-2">
            <p className="text-muted-foreground font-semibold mb-3 text-sm uppercase tracking-wider">
              O que você vai receber:
            </p>
            <ul className="space-y-1.5 md:space-y-2.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm md:text-base text-foreground">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-3 md:pt-6 pb-6 md:pb-12" id="hero" data-headline-variant={variant}>
      <div className="container max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* LEFT: text */}
          <div className="text-center md:text-left order-2 md:order-1">
            <h1
              className={`${variant === "B" ? "text-[1.35rem] md:text-[2.6rem] mb-5 md:mb-6" : "text-2xl md:text-5xl mb-3"} font-black leading-tight text-foreground drop-shadow-[0_2px_10px_hsl(22_93%_49%_/0.15)]`}
            >
              {renderHeadline()}
            </h1>

            {/* Bullets */}
            <div className="text-left max-w-md mx-auto md:mx-0 mb-6">
              <p className="text-muted-foreground font-semibold mb-3 text-sm uppercase tracking-wider">
                O que você vai receber:
              </p>
              <ul className="space-y-1.5 md:space-y-2.5">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm md:text-base text-foreground">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA with inline price */}
            <div className="flex flex-col items-center md:items-start gap-3">
              <p className="text-muted-foreground text-base md:text-lg">
                <span className="line-through">De R$479</span>{" "}
                <span className="text-3xl md:text-4xl font-black text-[hsl(var(--cta))] drop-shadow-[0_0_12px_hsl(142_71%_38%_/_0.5)]">
                  por R$27,00
                </span>
              </p>
              <CTAButton
                size="lg"
                href="#cta-final"
                className="text-xl md:text-2xl [text-shadow:_0_1px_4px_rgba(0,0,0,0.25)]"
              >
                Garantir Ingresso | 1º Lote
              </CTAButton>
            </div>
          </div>

          {/* RIGHT: image */}
          <div className="order-1 md:order-2 flex justify-center md:justify-end">
            <div
              className="w-40 h-40 sm:w-56 sm:h-56 md:w-full md:h-auto md:aspect-square md:max-w-md rounded-2xl overflow-hidden border-2 border-primary/30 shadow-lg"
              style={{ boxShadow: "0 0 40px hsl(22 93% 49% / 0.25)" }}
            >
              <img
                src={heroPhoto}
                alt="Foto do instrutor"
                width={512}
                height={512}
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover object-top brightness-110"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
